import { ChangeDetectionStrategy, Component, inject, input, OnDestroy } from '@angular/core';
import {
  DECLARATIVE_MAP_IMPLEMENTATION,
  DeclarativeMapImplementation,
  PaintArrow,
  PaintArrowDefinition,
} from '@cue/map/core';
import { NavDirectionItem } from '@cue/data';
import { explicitEffect } from 'ngxtension/explicit-effect';

@Component({
  selector: 'cue-map-arrow',
  imports: [],
  templateUrl: './map-arrow.component.html',
  styleUrl: './map-arrow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapArrowComponent implements OnDestroy {
  mapImplementation = inject(DECLARATIVE_MAP_IMPLEMENTATION);
  selectedDirections = input<NavDirectionItem[]>([]);
  directions = input<NavDirectionItem[]>([]);
  color = input<string>('#820B8A');

  paintItAll = explicitEffect([this.selectedDirections, this.directions, this.color], ([selectedDirections]) => {
    if (!selectedDirections || selectedDirections.length === 0) {
      this.clear();
    } else {
      this.paintSelectedDirection();
    }
  });

  private arrowPainter = this.resolveArrowPainter(this.mapImplementation);

  private getPartOfPath(
    firstX: number,
    firstY: number,
    secondX: number,
    secondY: number
  ): {
    pointX: number;
    pointY: number;
  } {
    return {
      pointX: firstX + (secondX - firstX) / 4,
      pointY: firstY + (secondY - firstY) / 4,
    };
  }

  private paintSelectedDirectionsRoute() {
    const pointlist: PaintArrowDefinition['pointlist'] = [];
    const selectedDirections = this.selectedDirections();
    const directions = this.directions();
    let partOfLastPath = {
      pointX: selectedDirections[0].third.x,
      pointY: selectedDirections[0].third.y,
    };

    selectedDirections.forEach((selectedDirection, index) => {
      //start
      pointlist.push({
        lat: selectedDirection.first.x,
        lng: selectedDirection.first.y,
      });

      //u posledniho bodu vemu jeste cast navazujici dalsi cesty aby sipka nebyla nalepena v posledn9m bodu ale lepe naznacovala smer otoceni
      if (selectedDirections.length > 1 && index === selectedDirections.length - 1) {
        //jestli je posledni bod uz portal do dalsi area tak nepridavat dalsi subbod
        if (selectedDirection.isPortal && selectedDirection.startAreaId != selectedDirection.endAreaId) {
          partOfLastPath = {
            pointX: selectedDirections[selectedDirections.length - 1].first.x,
            pointY: selectedDirections[selectedDirections.length - 1].first.y,
          };
          return;
        }

        partOfLastPath = this.getPartOfPath(
          selectedDirection.first.x,
          selectedDirection.first.y,
          directions[selectedDirection.index + 1]?.first.x ?? selectedDirection.second.x,
          directions[selectedDirection.index + 1]?.first.y ?? selectedDirection.second.y
        );

        //end
        if (Number.isFinite(partOfLastPath.pointX) && Number.isFinite(partOfLastPath.pointY)) {
          pointlist.push({
            lat: partOfLastPath.pointX,
            lng: partOfLastPath.pointY,
          });
        } else {
          partOfLastPath.pointX = selectedDirection.second.x;
          partOfLastPath.pointY = selectedDirection.second.y;
          pointlist.push({
            lat: partOfLastPath.pointX,
            lng: partOfLastPath.pointY,
          });
        }
      } else if (!selectedDirection.isPortal && selectedDirection.startAreaId == selectedDirection.endAreaId) {
        pointlist.push({
          lat: directions[selectedDirection.index + 1]?.first.x ?? selectedDirection.second.x,
          lng: directions[selectedDirection.index + 1]?.first.y ?? selectedDirection.second.y,
        });
      }
    });

    let start = {
      lat: selectedDirections[selectedDirections.length - 1].first.x,
      lng: selectedDirections[selectedDirections.length - 1].first.y,
    };
    const end = {
      lat: partOfLastPath.pointX,
      lng: partOfLastPath.pointY,
    };

    if (start.lng === end.lng && start.lat === end.lat && selectedDirections.length >= 2) {
      start = {
        lat: selectedDirections[selectedDirections.length - 2].first.x,
        lng: selectedDirections[selectedDirections.length - 2].first.y,
      };
    }

    this.arrowPainter.paintArrow({
      color: this.color(),
      pointlist,
      start,
      end,
    });
  }

  private clear() {
    this.arrowPainter.clearArrow();
  }

  private paintSelectedDirection() {
    this.clear();
    this.paintSelectedDirectionsRoute();
  }

  private resolveArrowPainter(mapImplementation: DeclarativeMapImplementation): PaintArrow {
    if ('paintArrow' in mapImplementation && 'clearArrow' in mapImplementation) {
      return mapImplementation as DeclarativeMapImplementation & PaintArrow;
    }

    throw new Error('Configured map implementation does not support arrow painting.');
  }

  ngOnDestroy() {
    this.clear();
  }
}
